import { useRef, useState, type ReactNode } from "react";
import { Pencil } from "lucide-react";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/ui/user-avatar";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink";
import { FileTypeIcon } from "@/components/chat/SidebarChat/DocLink/fileTypeIcon";
import { canPreviewFile } from "@/utils/previewUtils";
import type { AttributeSourceUser } from "@/hooks/buildings/useBuildingAttributes";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { isConfirmedSource, sourceKind, toAvatarUser, type HeaderFieldSpec } from "./fields";

/** The source document the value came from — clickable when it can be previewed. */
function SourceFileChip(props: { filename: string; onSourceClick?: (filename: string) => void }) {
  const { filename, onSourceClick } = props;
  const previewable = canPreviewFile(filename);

  return (
    <button
      type="button"
      className={`flex items-center gap-1.5 ${previewable ? "cursor-pointer hover:text-neutral-900" : ""}`}
      onClick={() => onSourceClick?.(filename)}
      disabled={!previewable}
    >
      <FileTypeIcon filename={filename} size={13} />
      <span>{filename}</span>
    </button>
  );
}

/** "Edited by: {name}" / "Confirmed by: {name}" with the editor's avatar. */
function SourceUserLabel(props: {
  source: string;
  sourceUser: AttributeSourceUser;
  tw: BuildingDataWidgetSections;
}) {
  const { source, sourceUser, tw } = props;

  return (
    <span className="flex items-center gap-1.5 font-medium">
      <UserAvatar user={toAvatarUser(sourceUser)} size={16} />
      <span>
        {isConfirmedSource(source) ? tw.confirmedBy : tw.editedBy}: {sourceUser.name}
      </span>
    </span>
  );
}

function FieldSourceTooltip(props: {
  source: string;
  sourceUser?: AttributeSourceUser | null;
  ehrUrl?: string;
  tw: BuildingDataWidgetSections;
  onSourceClick?: (filename: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const { source, sourceUser, ehrUrl, tw, onSourceClick, onMouseEnter, onMouseLeave } = props;
  const kind = sourceKind(source);

  return (
    <div
      className="absolute left-0 top-full z-50 mt-1 whitespace-nowrap rounded-lg border bg-white p-2 shadow-md"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center gap-1.5 text-xs text-neutral-700">
        {kind === "file" ? (
          <SourceFileChip filename={source} onSourceClick={onSourceClick} />
        ) : kind === "ehr" && ehrUrl ? (
          <EhrChip url={ehrUrl} />
        ) : kind === "user" && sourceUser ? (
          <SourceUserLabel source={source} sourceUser={sourceUser} tw={tw} />
        ) : (
          <span className="font-medium">{source}</span>
        )}
      </div>
    </div>
  );
}

interface FieldItemProps {
  field: HeaderFieldSpec;
  ehrUrl?: string;
  onSourceClick?: (filename: string) => void;
  /** Accessible label for the inline-edit affordance, e.g. "Edit". */
  editLabel: string;
  tw: BuildingDataWidgetSections;
  /** Whether this field's edit popover is open (controlled — one at a time). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The field's edit popover content (a PopoverContent). Mounted only while
   * open so its draft state re-initializes from the current value each time. */
  children: ReactNode;
}

/** One value on the metadata row: label, static value, hover source tooltip,
 * and the anchor for the field's edit popover. */
export function FieldItem(props: FieldItemProps) {
  const { field, ehrUrl, onSourceClick, editLabel, tw, open, onOpenChange, children } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

  const show = () => {
    clearTimeout(hideTimeout.current);
    setShowTooltip(true);
  };
  const hideDelayed = () => {
    hideTimeout.current = setTimeout(() => setShowTooltip(false), 150);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <div
        className="group relative flex flex-col gap-0.5"
        onMouseEnter={show}
        onMouseLeave={hideDelayed}
      >
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {field.label}
        </span>
        {/* The value itself is the edit affordance: hovering tints it and
            reveals a pencil; clicking (or tapping, on touch) opens the field's
            edit popover. Keyboard users reach it via the button's native focus. */}
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${editLabel}: ${field.label}`}
            className="-mx-1.5 -my-0.5 flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors focus-visible:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 group-hover:bg-muted/70"
          >
            <span className="text-[13px] font-medium text-neutral-800">{field.display}</span>
            <Pencil className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100" />
          </button>
        </PopoverTrigger>
        {showTooltip && !open && field.source && (
          <FieldSourceTooltip
            source={field.source}
            sourceUser={field.sourceUser}
            ehrUrl={ehrUrl}
            tw={tw}
            onSourceClick={onSourceClick}
            onMouseEnter={show}
            onMouseLeave={hideDelayed}
          />
        )}
      </div>
      {open && children}
    </Popover>
  );
}
